// @ts-strict-ignore
import React, { useMemo, useState } from 'react';
import {
  Banner, Box, IconName, Slider,
} from '@usealloy/component-library';

import type { RuleBreakdownProps } from '../RuleCard/RuleBreakdown';

import ThresholdsLineChart from './ThresholdsLineChart';
import { GREEN, SLIDER_MARKER_THRESHOLD } from './constants';
import type { ChartLevels } from './types';
import { convertChartLevelsToSliderMarks } from './helpers';


type BreakdownChartProps = {
  breakdownMap: Map<number, number>;
  chartLevels: ChartLevels;
  sliderPercentile: number;
  setSliderPercentile: (arg: number) => void;
  thresholdOperator: string;
  thresholdType: RuleBreakdownProps['thresholdType'];
};

const BreakdownChart: React.FC<BreakdownChartProps> = ({
  breakdownMap,
  chartLevels,
  sliderPercentile,
  setSliderPercentile,
  thresholdOperator,
  thresholdType,
}) => {
  const hasLimitedSliderMarks = Object.keys(chartLevels).length <= SLIDER_MARKER_THRESHOLD;
  const [isSliderBannerVisible, setIsSliderBannerVisible] = useState(hasLimitedSliderMarks);
  const operatorFlipped = ['ge', 'gt'].includes(thresholdOperator);
  const onSliderChange = (val: number) => setSliderPercentile(operatorFlipped ? 99 - val : val);
  const sliderValue = operatorFlipped ? 99 - sliderPercentile : sliderPercentile;
  const sliderMarks = useMemo(
    () => convertChartLevelsToSliderMarks(chartLevels, sliderValue, thresholdOperator),
    [chartLevels, sliderValue, thresholdOperator],
  );
  return (
    <Box direction="column" alignItems="flex-end">
      <Box style={{ height: 275 }}>
        <ThresholdsLineChart
          bandValue={sliderPercentile}
          breakdownMap={breakdownMap}
          chartLevels={chartLevels}
          thresholdOperator={thresholdOperator}
          thresholdType={thresholdType}
        />
      </Box>
      <Box
        width="756px"
        style={{
          height: 'fit-content',
          marginRight: '40px',
        }}
      >
        <Slider
          onChange={onSliderChange}
          marks={sliderMarks}
          max={99}
          min={1}
          trackColors={[{ backgroundColor: GREEN }]}
          value={sliderValue}
          {...(Object.keys(sliderMarks).length <= 3 ? {} : { step: null })}
        />
      </Box>
      <Banner
        icon={IconName.Info}
        onDismiss={() => setIsSliderBannerVisible(false)}
        open={isSliderBannerVisible}
        style={{ marginTop: '34px' }}
        variant="info"
      >
        The slider only shows percentages that impact the results because of limited
        score variation.
      </Banner>
    </Box>
  );
};


export default BreakdownChart;
